/**
 * Pure helpers behind the replicas chart's "used" stripes (TRA-1437): the
 * rows the chart draws, the stripe series, the hover words and the rows of
 * the per-window usage table. No DOM, so they are testable as they are.
 *
 * The api (services/replica_usage.py) hands the chart one list of load
 * steps: how many requests were running from each step's start for its
 * length, and the concurrency target in force there. A step is Baseten's
 * own 2-minute count where the gateway has it, else the mean of the
 * 15-minute usage cell; the merge lives in the api, and the chart only
 * looks up the step covering each row. The chart puts that load against
 * the replicas running at each row: busy replicas are the load over the
 * target, capped at the replicas running, and a row is full when the load
 * reached every running replica's target, so requests waited.
 */

import { formatDays, formatHours } from '@/components/models/home/scheduleWindows'
import type {
  ModelScalingResponse,
  ModelScalingUsageResponse,
  ModelWindowUsage,
} from '@/hooks/useModels'

type Sample = ModelScalingResponse['history'][number]

export interface ReplicaChartRow {
  epoch: number
  /** Replicas running at this instant; null before the first sample. */
  replicas: number | null
  status: string | null
  /** Replicas busy at this instant: the load over the concurrency target
   *  in force, capped at the replicas running. Null when no step covers
   *  the instant or nothing was running. */
  busy: number | null
  /** The busy share as a whole percent, capped at 100; null with `busy`. */
  percentUsed: number | null
  /** Every running replica was at its target, so requests waited. */
  full: boolean
}

interface SampleAt {
  epoch: number
  replicas: number
  status: string
}

interface StepAt {
  epoch: number
  /** The first instant after the step. */
  end: number
  load: number
  concurrencyTarget: number
}

function byEpoch<T extends { epoch: number }>(a: T, b: T): number {
  return a.epoch - b.epoch
}

/** The index of the last entry at or before `epoch`, from `from` onward. */
function lastAtOrBefore(
  entries: readonly { epoch: number }[],
  from: number,
  epoch: number,
): number {
  let index = from
  for (let next = index + 1; next < entries.length; next += 1) {
    const entry = entries[next]
    if (entry === undefined || entry.epoch > epoch) break
    index = next
  }
  return index
}

/** Slack for float noise on a whole ratio, the same as the api's. */
const FLOAT_TOLERANCE = 1e-9

/**
 * The share of the running replicas' concurrency target the load took,
 * unbounded: 1 is every running replica at its full target. Null when the
 * load or the replica count is unknown, or nothing was running.
 */
function replicaUse(
  load: number | null,
  replicas: number | null,
  concurrencyTarget: number | null,
): number | null {
  if (load === null || replicas === null || replicas === 0) return null
  if (concurrencyTarget === null) return null
  return load / (replicas * concurrencyTarget)
}

/** Replicas busy: the load over the target, never more than were running. */
export function busyReplicas(
  load: number | null,
  replicas: number | null,
  concurrencyTarget: number | null,
): number | null {
  const use = replicaUse(load, replicas, concurrencyTarget)
  if (use === null || replicas === null) return null
  return Math.min(replicas, use * replicas)
}

/** The busy share as a whole percent, capped at 100. */
export function percentUsed(
  load: number | null,
  replicas: number | null,
  concurrencyTarget: number | null,
): number | null {
  const use = replicaUse(load, replicas, concurrencyTarget)
  return use === null ? null : Math.min(100, Math.round(100 * use))
}

/** Every running replica was at its target, so requests waited. */
export function isFull(
  load: number | null,
  replicas: number | null,
  concurrencyTarget: number | null,
): boolean {
  const use = replicaUse(load, replicas, concurrencyTarget)
  return use !== null && use >= 1 - FLOAT_TOLERANCE
}

/**
 * One row per replica sample, per load step and per step end, sorted by
 * time. Each row carries the replica level in force at its instant and the
 * busy replicas from the step covering its instant, so the stripes follow
 * the Running line, change where the load does, stop where a step ends
 * with none after it, and the hover box shows the percent used next to
 * the level. A step end past the last sample and step adds no row: it
 * would only stretch the chart.
 */
export function mergeSeries(
  history: readonly Sample[],
  usage: ModelScalingUsageResponse | null,
): ReplicaChartRow[] {
  const samples: SampleAt[] = history
    .map((s) => ({ epoch: new Date(s.timestamp).getTime(), replicas: s.replica_count, status: s.status }))
    .sort(byEpoch)
  const steps: StepAt[] = (usage?.steps ?? [])
    .map((s) => {
      const epoch = new Date(s.start).getTime()
      return { epoch, end: epoch + s.seconds * 1000, load: s.load, concurrencyTarget: s.concurrency_target }
    })
    .sort(byEpoch)
  const known = [...samples, ...steps].map((e) => e.epoch)
  const last = Math.max(...known)
  const ends = steps.map((s) => s.end).filter((end) => end < last)
  const epochs = [...new Set([...known, ...ends])].sort((a, b) => a - b)
  let sampleIndex = -1
  let stepIndex = -1
  return epochs.map((epoch) => {
    sampleIndex = lastAtOrBefore(samples, sampleIndex, epoch)
    stepIndex = lastAtOrBefore(steps, stepIndex, epoch)
    const sample = samples[sampleIndex]
    const step = steps[stepIndex]
    const inStep = step !== undefined && epoch < step.end
    const replicas = sample?.replicas ?? null
    const load = inStep ? step.load : null
    const target = inStep ? step.concurrencyTarget : null
    return {
      epoch,
      replicas,
      status: sample?.status ?? null,
      busy: busyReplicas(load, replicas, target),
      percentUsed: percentUsed(load, replicas, target),
      full: isFull(load, replicas, target),
    }
  })
}

/** The chart's top: the highest replica level on it, at least 1. */
export function chartPeak(rows: readonly ReplicaChartRow[]): number {
  let peak = 1
  for (const row of rows) {
    if (row.replicas !== null) peak = Math.max(peak, row.replicas)
  }
  return peak
}

type StripeKind = 'used' | 'full'
export type StripeKey = `${StripeKind}${0 | 1}`
export const STRIPE_KEYS: Record<StripeKind, readonly [StripeKey, StripeKey]> = {
  used: ['used0', 'used1'],
  full: ['full0', 'full1'],
}

export type StripedRow = ReplicaChartRow & Record<StripeKey, number | null>

/**
 * The stripe series the chart draws as stepped areas: a run of rows of one
 * kind (used, or full in red) is one stretch of stripes. A stepped area
 * draws a segment between two consecutive rows only when both hold a
 * value, so each run also sets the row after its last one, flat at its
 * last level, and the next run starts at that same row: no gap where the
 * colours meet. A run of a kind goes in the kind's other series from the
 * run before it, so no series ever holds two runs on adjacent rows, which
 * would draw a stretch that is not there. A row with no busy value ends a
 * run and draws no stripes after it.
 */
export function stripeSeries(rows: readonly ReplicaChartRow[]): StripedRow[] {
  const slot: Record<StripeKind, 0 | 1> = { used: 1, full: 1 }
  let current: StripeKey | null = null
  let previous: ReplicaChartRow | undefined
  return rows.map((row) => {
    const out: StripedRow = { ...row, used0: null, used1: null, full0: null, full1: null }
    if (current !== null && previous !== undefined) out[current] = previous.busy
    if (row.busy === null) {
      current = null
    } else {
      const kind: StripeKind = row.full ? 'full' : 'used'
      if (current === null || !current.startsWith(kind)) {
        slot[kind] = slot[kind] === 0 ? 1 : 0
        current = STRIPE_KEYS[kind][slot[kind]]
      }
      out[current] = row.busy
    }
    previous = row
    return out
  })
}

export const LEGEND_LABELS = {
  running: 'Running',
  used: 'Used',
  full: 'Full, requests waited',
} as const

/**
 * The hover box's lines after the time: the replicas running and, when the
 * instant's use is known, the percent used. The busy count only draws the
 * stripes; it is never shown as text. A full instant adds a second line.
 */
export function hoverLines(row: ReplicaChartRow): string[] {
  const running = `${row.replicas === null ? '—' : String(row.replicas)} running`
  if (row.replicas === null || row.percentUsed === null) return [running]
  const line = `${running} · ${String(row.percentUsed)}% used`
  return row.full ? [line, LEGEND_LABELS.full] : [line]
}

/** One decimal, or the empty cell: the running average in the window table. */
export function formatEstimate(value: number | null): string {
  return value === null ? '—' : value.toFixed(1)
}

/** A whole percent, or the empty cell. */
export function formatWholePercent(value: number | null): string {
  return value === null ? '—' : `${String(Math.round(value))}%`
}

/** "0 min", "45 min", "2 h", "2 h 15 min", or the empty cell. */
export function formatMinutes(minutes: number | null): string {
  if (minutes === null) return '—'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${String(rest)} min`
  return rest === 0 ? `${String(hours)} h` : `${String(hours)} h ${String(rest)} min`
}

export interface WindowUsageRow {
  key: string
  name: string
  days: string
  hours: string
  floor: string
  avgRunning: string
  avgUsed: string
  timeFull: string
}

/** The usage table's rows, formatted. A window with no data shows empty cells. */
export function windowUsageRows(windows: readonly ModelWindowUsage[]): WindowUsageRow[] {
  return windows.map((w) => ({
    key: w.schedule_id ?? w.name,
    name: w.name,
    days: formatDays(w.weekdays),
    hours: formatHours(w),
    floor: w.floor === null ? '—' : String(w.floor),
    avgRunning: formatEstimate(w.avg_running),
    avgUsed: formatWholePercent(w.avg_percent_used),
    timeFull: formatMinutes(w.minutes_full),
  }))
}
